import { useRef, useState } from "react"
import type { ClipboardEvent, KeyboardEvent } from "react"

const createEmptyVerificationCode = (length: number) => Array<string>(length).fill("")

export const useVerificationCode = (length: number) => {
  const [verificationCodeDigits, setVerificationCodeDigits] = useState<string[]>(createEmptyVerificationCode(length))
  const verificationCodeInputRefs = useRef<Array<HTMLInputElement | null>>([])
  const verificationCode = verificationCodeDigits.join("")

  const resetVerificationCode = () => {
    setVerificationCodeDigits(createEmptyVerificationCode(length))
  }

  const focusFirstVerificationCodeInput = () => {
    verificationCodeInputRefs.current[0]?.focus()
  }

  const handleVerificationCodeDigitChange = (index: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, "")
    if (!digitsOnly) {
      const updatedDigits = [...verificationCodeDigits]
      updatedDigits[index] = ""
      setVerificationCodeDigits(updatedDigits)
      return
    }

    const updatedDigits = [...verificationCodeDigits]
    let cursor = index

    for (const digit of digitsOnly) {
      if (cursor >= length) break
      updatedDigits[cursor] = digit
      cursor += 1
    }

    setVerificationCodeDigits(updatedDigits)

    if (cursor < length) {
      verificationCodeInputRefs.current[cursor]?.focus()
    } else {
      verificationCodeInputRefs.current[length - 1]?.focus()
    }
  }

  const handleVerificationCodeKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !verificationCodeDigits[index] && index > 0) {
      verificationCodeInputRefs.current[index - 1]?.focus()
    }
  }

  const handleVerificationCodePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()

    const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length)
    if (!pastedDigits) {
      return
    }

    const updatedDigits = createEmptyVerificationCode(length)
    pastedDigits.split("").forEach((digit, index) => {
      updatedDigits[index] = digit
    })

    setVerificationCodeDigits(updatedDigits)

    const focusIndex = Math.min(pastedDigits.length, length - 1)
    verificationCodeInputRefs.current[focusIndex]?.focus()
  }

  return {
    verificationCode,
    verificationCodeDigits,
    verificationCodeInputRefs,
    resetVerificationCode,
    focusFirstVerificationCodeInput,
    handleVerificationCodeDigitChange,
    handleVerificationCodeKeyDown,
    handleVerificationCodePaste,
  }
}